import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import type { ClientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing.ts';
import isEmpty from 'lodash/isEmpty';
import type { TaxonomyTreeSegmentFragment } from '../../../graphql/fragment/taxonomy/TaxonomyTreeSegment.generated.tsx';

import type { TaxonomyListFilters } from '../types.ts';

type TaxonomyTreeElement = Pick<
  TaxonomyTreeSegmentFragment,
  'segment_type' | 'segment_category' | 'segment_sub_category' | 'segment_source' | 'id'
> &
  Partial<Pick<TaxonomyTreeSegmentFragment, 'path'>>;

type Node = TaxonomyTreeElement & {
  children: Node[] | null;
} & Partial<{
    is_leaf: boolean;
    is_nav_only: boolean;
    name: string;
  }>;

export const mapTaxonomyMetadataToTree = (segments: TaxonomyTreeElement[]) => {
  const segmentsWithFixedPath: TaxonomyTreeElement[] = segments.map(segment => ({
    ...segment,
    path: [segment.segment_type, segment.segment_category, segment.segment_sub_category].filter(n => n),
  }));

  function buildHierarchy(data: TaxonomyTreeElement[]): Node[] {
    const tree: Node[] = [];

    data.forEach(segment => {
      let currentLevel = tree;

      segment.path.forEach((level: string, index: number) => {
        let existingNode = currentLevel.find(node => node.name === level);
        const isLastPathNode = segment.path.length - 1 === index;

        if (!existingNode) {
          existingNode = {
            children: [],
            id: `${segment.id}-${level}`,
            is_leaf: false,
            is_nav_only: true,
            name: level,
            segment_category: index >= 1 ? segment.segment_category : '',
            segment_source: segment.segment_source,
            segment_sub_category: index >= 2 ? segment.segment_sub_category : '',
            segment_type: segment.segment_type,
          };
          currentLevel.push(existingNode);
        }

        if (isLastPathNode && !existingNode.children?.length) {
          existingNode.children = null;
        }

        if (existingNode.children) currentLevel = existingNode.children;
      });
    });

    return tree;
  }

  return buildHierarchy(segmentsWithFixedPath);
};

type RecursiveTreeSortParams = {
  tree: Node[];
  sotringMethod: ClientSideCriteriaProcessing;
  criteria: AllTableCriteria<TaxonomyListFilters>;
  setCriteria: (criteria: AllTableCriteria<TaxonomyListFilters>) => void;
};

export function recursiveTreeSort({ criteria, setCriteria, sotringMethod, tree }: RecursiveTreeSortParams) {
  return sotringMethod(tree, criteria, { onCriteriaAdjust: setCriteria }).map(item => {
    if (isEmpty(item.children)) return item;
    return {
      ...item,
      children: recursiveTreeSort({ criteria, setCriteria, sotringMethod, tree: item.children ?? [] }),
    };
  });
}
